import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Database, Package, Image as ImageIcon, BarChart } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresERPShowcase: React.FC = () => {
  const { t } = useTranslation('bares')

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const images = [
    { src: '/images/bares/15-erp-produtos-desktop.png', alt: 'ERP - Produtos', title: 'ERP - Produtos', description: 'Cadastro e listagem de produtos.' },
    { src: '/images/bares/15a-erp-produtos-detalhes.png', alt: 'ERP - Detalhes', title: 'ERP - Detalhes do produto', description: 'Informações completas do item.' },
    { src: '/images/bares/15b-erp-produtos-skus.png', alt: 'ERP - SKUs', title: 'ERP - SKUs', description: 'Variações e códigos.' },
    { src: '/images/bares/15c-erp-produtos-midia.png', alt: 'ERP - Mídia', title: 'ERP - Mídia', description: 'Imagens e galeria do item.' }
  ]

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <Database className="w-4 h-4" />
            {t('erp.kicker', 'Gestão Integrada')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            ERP Integrado (Produtos)
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Cadastre produtos, categorias e preços e mantenha tudo sincronizado com o cardápio digital.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {images.map((img, i) => {
            const icons = [Package, BarChart, Package, ImageIcon]
            const titles = ['Lista de Produtos', 'Detalhes', 'SKUs', 'Mídia']
            const colors = [
              { bg: 'bg-gradient-to-br from-amber-50 to-orange-50', border: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
              { bg: 'bg-gradient-to-br from-orange-50 to-yellow-50', border: 'border-orange-200', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
              { bg: 'bg-gradient-to-br from-yellow-50 to-amber-50', border: 'border-yellow-200', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600' },
              { bg: 'bg-gradient-to-br from-amber-50 to-orange-50', border: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' }
            ]
            const Icon = icons[i]
            const colorScheme = colors[i]

            return (
              <motion.button
                key={img.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`group ${colorScheme.bg} rounded-2xl border-2 ${colorScheme.border} p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
                onClick={() => { setIndex(i); setOpen(true) }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 ${colorScheme.iconBg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${colorScheme.iconColor}`} />
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">{titles[i]}</span>
                </div>
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full rounded-lg border border-white shadow-sm group-hover:scale-[1.02] transition-transform"
                />
              </motion.button>
            )
          })}
        </div>

        {/* Features Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6 text-amber-600" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Cadastro Completo</h4>
            <p className="text-sm text-gray-600">Produtos, categorias, preços e variações</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl border border-orange-200 p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <ImageIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Galeria de Imagens</h4>
            <p className="text-sm text-gray-600">Fotos de alta qualidade para o cardápio</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200 p-6 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Database className="w-6 h-6 text-yellow-600" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Sincronização Automática</h4>
            <p className="text-sm text-gray-600">Alterações refletem instantaneamente no site</p>
          </div>
        </motion.div>
      </div>
      <ImageGallery images={images} isOpen={open} onClose={() => setOpen(false)} initialIndex={index} />
    </section>
  )
}
